import React, { FC, MouseEvent, useEffect, useRef, useState, ReactElement, Component } from 'react';
import ChatPopupHeader from '../chat-popup/chat-popup-header';
import classnames from 'classnames';
import { IWindowResolutionInfo } from '../../../store/podium-info-store/podium-info-store';

interface IProps {
  isVisible: boolean;
  resolution: IWindowResolutionInfo;
  isMobile: boolean;
  onClose: () => void;
  children?: Component | ReactElement;
  messagesCount: number;
}

const ChatResizeContainer: FC<IProps> = ({ isVisible, resolution, isMobile, onClose, children, messagesCount }): JSX.Element => {
  const [mouseDown, setMouseDown] = useState<boolean>(false);
  const [initialPos, setInitialPos] = useState<number | null>(null);
  const [initialSize, setInitialSize] = useState<number | null>(null);
  const [chatBoxHeight, setChatBoxHeight] = useState<number | null>(null);
  const [chatBoxMaxHeight, setChatBoxMaxHeight] = useState<number | null>(null);
  const [shrinkHeaderHeight, setShrinkHeaderHeight] = useState<number | null>(null);

  const chatBoxRef = useRef<HTMLDivElement>(null);
  const chatBox = chatBoxRef.current!;

  const headerShrink = 0;
  const display = isVisible ? 'flex' : 'none';

  const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    if (!chatBox) return;

    setInitialPos(e.clientY);
    setInitialSize(chatBox.offsetHeight);
    setMouseDown(true);
  };

  const setChatBoxHeightOnMobileDevices = (): void => {
    if(!(isMobile && chatBox)) return;
    chatBox.style.height = `${resolution.height - shrinkHeaderHeight!}px`;
  };

  useEffect(() => {
    if (!chatBox) return;

    const chatBoxStyle = getComputedStyle(chatBox);
    const header = document.getElementsByClassName('kv-presenter-header')[0] as HTMLDivElement;
    const globalHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
    const availableHeight = globalHeight - parseInt(chatBoxStyle.bottom);
    const headerHeight = header?.offsetHeight ? header.offsetHeight : 0;
    const maxChatHeight = headerHeight ? availableHeight - headerHeight : availableHeight;
    setShrinkHeaderHeight(headerHeight - headerShrink);
    setChatBoxHeight(parseInt(chatBoxStyle.height));
    setChatBoxMaxHeight(maxChatHeight);
  }, [chatBox]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
      const height = initialSize! + (initialPos! - e.clientY);
      if (height < chatBoxHeight!) {
        chatBox.style.height = `${chatBoxHeight}px`;
      } else if (height > chatBoxMaxHeight!) {
        chatBox.style.height = `${chatBoxMaxHeight}px`;
      } else {
        chatBox.style.height = `${height}px`;
      }
    };

    if (mouseDown) {
      // @ts-ignore
      document.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      // @ts-ignore
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [mouseDown]);

  useEffect(() => {
    setChatBoxHeightOnMobileDevices();
  }, [resolution, isVisible]);

  useEffect(() => {
    const handleMouseUp = () => setMouseDown(false);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);
  return (
    <div className={classnames('chat-box', { 'select-blocked': mouseDown })} ref={chatBoxRef} style={{ display }}>
      <ChatPopupHeader isMobile={isMobile} onClose={onClose} handleMouseDown={handleMouseDown} messagesCount={messagesCount}/>
      {children}
    </div>
  );
};

export default ChatResizeContainer;
